import { CommandItem, CommandSeparator } from '@/components/ui/Command.tsx'
import Paragraph from '@/components/common/textElements/Paragraph.tsx'
import { cn } from '@/lib/utils.ts'
import { ReactNode } from 'react'
import { usePropertiesStore } from '@/components/common/propertyPopoverOverlay/usePropertiesStore.ts'
import { Check, ChevronRight } from 'lucide-react'
import useLocationMapStore from '@/components/common/location/store.ts'

export type ParentT = 'statuses' | 'creators' | 'recipients' | 'locations' | 'labels'

export type PopoverProperty = {
  label: string
  description?: string | null
  value: string
  icon?: ReactNode
  labelNode?: ReactNode
  children?: PopoverProperty[] | null
  customFilter?: string
  secondIcon?: ReactNode
  id?: string
  component?: ReactNode
  mapCode?: string
  disabled?: boolean
  parent?: string
  searchableValue?: string
}

type PropsT = {
  property: PopoverProperty
  setCurrentProperties: (properties: PopoverProperty[]) => void
  handleOpenChange: (open: boolean) => void
  closeOnSelect?: boolean
  selectedValues: string[]
  addSelectedValue: (value: string) => void
  removeSelectedValue?: (value: string) => void
  selectAll?: () => void
  parent: 'popover' | 'overlay'
  handleSelectParent: (property: PopoverProperty) => void
  renderAsButton?: boolean
}

export default function Property({
  property,
  handleOpenChange,
  closeOnSelect,
  selectedValues,
  addSelectedValue,
  removeSelectedValue,
  selectAll,
  parent,
  handleSelectParent,
  renderAsButton,
}: PropsT) {
  const deletedPropertiesIds = usePropertiesStore(state => state.deletedPropertiesIds)
  const setHoveredLocationCode = useLocationMapStore(state => state.setHoveredLocationCode)

  if (deletedPropertiesIds.includes(property.id as string)) return

  if (property.label === 'separator') {
    if (renderAsButton) return <div className={`my-1 h-px bg-border`} role="separator" />
    return <CommandSeparator className={`my-1`} />
  }
  if (property.label === 'component') return property.component

  const handleSelect = () => {
    if (property.value === 'select-all' && selectAll) {
      return selectAll()
    }

    if (property.children) {
      return handleSelectParent(property)
    }

    if (selectedValues.includes(property.value)) {
      removeSelectedValue && removeSelectedValue(property.value)
    } else if (!property.disabled) {
      addSelectedValue(property.value)
    }

    if (closeOnSelect) handleOpenChange(false)
  }

  const onMouseEnter = () => {
    if (property.mapCode) {
      setHoveredLocationCode(property.mapCode)
    }
  }

  const onMouseLeave = () => {
    if (property.mapCode) {
      setHoveredLocationCode(null)
    }
  }
  const isSelected = selectedValues.some(
    selectedValue => property.value && selectedValue == property.value,
  )

  const content = (
    <div
      className={cn(
        `flex w-full items-center justify-between rounded hover:bg-accent md:max-w-[360px]`,
        parent === 'overlay' ? 'min-h-[52px] gap-3 px-3 py-3' : 'px-2 py-1.5',
      )}
    >
      <div className={cn(`flex items-center gap-2`, parent === 'overlay' && 'w-full gap-3')}>
        {property.icon && property.icon}
        <div className={`w-full`}>
          <Paragraph
            className={`max-w-[80vw] truncate md:max-w-[260px]`}
            variant={'default'}
            size="medium"
          >
            {property.labelNode ? property.labelNode : property.label}
          </Paragraph>
          <Paragraph
            className={cn(
              `truncate`,
              parent === 'overlay' && isSelected && 'hidden',
              parent === 'overlay' && 'ml-auto',
            )}
            variant={'muted'}
            size="medium"
          >
            {property.description}
          </Paragraph>
        </div>
      </div>
      <div></div>
      <div className={`flex items-center gap-2`}>
        {isSelected && !property.value.includes(`all`) && (
          <Check className={cn(`size-4`, parent === 'overlay' && 'size-5')} />
        )}
        {property.secondIcon && property.secondIcon}
        {property.children && <ChevronRight className={`size-4`} />}
      </div>
    </div>
  )

  if (renderAsButton) {
    return (
      <button
        type="button"
        onClick={handleSelect}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        disabled={property.disabled}
        className={cn(
          `relative flex w-full cursor-pointer select-none items-center gap-2 rounded-sm text-left text-base outline-none disabled:cursor-not-allowed disabled:opacity-50 lg:text-xs [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0`,
        )}
        data-cy={`property-popover-property`}
      >
        {content}
      </button>
    )
  }

  return (
    <>
      <CommandItem
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        value={property.label}
        disabled={property.disabled}
        onSelect={handleSelect}
        className={'cursor-pointer'}
        data-cy={`property-popover-property`}
      >
        {content}
      </CommandItem>
    </>
  )
}
